import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import SearchModal from '../components/search/SearchModal';

const SearchContext = createContext({
    openSearch: () => {},
    closeSearch: () => {},
});

/**
 * Global arama provider'ı: tek SearchModal'ı render eder + Cmd/Ctrl+K kısayolu.
 * Hem mobil BottomNav hem desktop header buton tetikleyici olarak kullanır.
 */
export function SearchProvider({ children }) {
    const [open, setOpen] = useState(false);
    // Mobil klavye için trick: iOS/Android sanal klavyesi yalnızca user-gesture
    // context'inde çağrılan focus()'larda açılır. Modal animasyonu bittikten sonra
    // (setTimeout) focus çağrısı bu context'in dışında kalıyor. Çözüm: kullanıcı
    // FAB butona tap'ladığı anda DOM'da hazır duran "yardımcı" bir input'a synchronous
    // focus yapıyoruz; klavye açılır. Modal sonra mount olur ve gerçek input'a focus
    // transfer edilir (SearchModal kendi useEffect'iyle).
    const helperInputRef = useRef(null);

    const openSearch = useCallback(() => {
        try {
            helperInputRef.current?.focus({ preventScroll: true });
        } catch { /* ignore */ }
        setOpen(true);
    }, []);
    const closeSearch = useCallback(() => setOpen(false), []);

    // Klavye kısayolu: Cmd+E (Mac) / Ctrl+E (Windows/Linux)
    // Browser default'u: adres çubuğunu arama moduna sokar — preventDefault ile bastırılır.
    useEffect(() => {
        const handler = (e) => {
            const isE = e.key === 'e' || e.key === 'E';
            if (isE && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                e.stopPropagation();
                setOpen(prev => !prev);
            }
        };
        // Capture phase'de bağla: input/textarea'daki handler'lar event'i tüketmeden önce yakalanır
        window.addEventListener('keydown', handler, true);
        return () => window.removeEventListener('keydown', handler, true);
    }, []);

    return (
        <SearchContext.Provider value={{ openSearch, closeSearch }}>
            {children}
            {/* Mobile keyboard primer — DOM'da hep mevcut, görünmez. tap anında focus alır. */}
            <input
                ref={helperInputRef}
                type="text"
                inputMode="search"
                aria-hidden="true"
                tabIndex={-1}
                readOnly
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: 1,
                    height: 1,
                    opacity: 0,
                    pointerEvents: 'none',
                    border: 'none',
                    background: 'transparent',
                    zIndex: -1,
                }}
            />
            <SearchModal open={open} onClose={closeSearch} />
        </SearchContext.Provider>
    );
}

export function useSearch() {
    return useContext(SearchContext);
}
